import { TccdCookieSettings } from './cookie-management';
import type { CookieReader } from '@/utils/cookies';
import { tryParseJson, type JsonValue } from '@/utils/json';

// Parses the `X-Experiments` request header set by uber-worker when it evaluates LaunchDarkly flags.
// The header is a flat JSON object mapping flag key -> LD variation value — see
// uber-worker's setExperimentHeader.ts for the producing side. No URL-encoding
// or base64 is applied.
const HOLDOUT_KEY = '__holdout';
const IN_EXPERIMENT_KEY = '__inExperiment';

export const REMOVE_DURATION_SELECTOR_FLAG_KEY = 'remove-duration-selector';

// Mirrors uber-worker's `LDInputType` (`string | boolean | object | number`) —
// LD variations can be JSON, so an object or array value is legitimate on the
// wire, not a malformed header. Values are kept as parsed and each accessor
// narrows at the point of use, so an unexpected shape can't silently vanish here.
export type ExperimentValue = JsonValue;
export type Experiments = Record<string, ExperimentValue>;

export const parseExperiments = (header: string | null): Experiments => {
  const parsed = header ? tryParseJson(header) : null;
  return parsed ?? {};
};

export const getExperimentBucket = (experiments: Experiments, flagKey: string): string | null => {
  const value = experiments[flagKey];
  return typeof value === 'string' ? value : null;
};

// `__holdout` is a sorted, comma-joined list of prerequisite flag keys whose
// evaluation excluded this visitor from the gated experiment(s) — i.e. the
// prerequisite's required variation wasn't met, not an evaluation error.
// Set once and shared across every experiment gated behind that prerequisite.
// Absent/empty means not held out.
export const getHoldoutName = (experiments: Experiments): string | null => {
  const value = experiments[HOLDOUT_KEY];
  return typeof value === 'string' && value.length > 0 ? value : null;
};

// `__inExperiment` is a sorted array of flag keys for which LD's variationDetail
// reported `reason.inExperiment === true` — i.e. this visitor's bucket came from
// a genuine, tracked LD Experiment evaluation, not a fallthrough/default. This
// disambiguates a flag's "control" bucket being a real experiment variation vs.
// just the flag's default. A visitor can be in multiple experiments at once, so
// (unlike `__holdout`'s comma-joined string) this is a JSON array on the wire.
export const isInExperiment = (experiments: Experiments, flagKey: string): boolean => {
  const value = experiments[IN_EXPERIMENT_KEY];
  return Array.isArray(value) && value.includes(flagKey);
};

export const getTccd = (cookies: Pick<CookieReader, 'get'>): string | undefined =>
  cookies.get(TccdCookieSettings.name)?.value;
